import React from 'react';
import { Loader2 } from 'lucide-react';
import { formatCurrency, getAmountClass, accountTypeLabels } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

export const PostConfirmDialog = ({ open, onOpenChange, approvedCount, onConfirm, posting }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Post All to General Ledger?</AlertDialogTitle>
        <AlertDialogDescription>
          This will create journal entries for all {approvedCount} approved transactions. 
          Once posted, transactions can only be reversed (not deleted).
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={posting} className="bg-[#D94E18] hover:bg-[#C2410C]" data-testid="confirm-post-all-btn">
          {posting ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Posting...</span> : 'Post All'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ApproveAllDialog = ({ open, onOpenChange, withAccount, withoutAccount, onConfirm, approving }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Approve All Pending Transactions?</AlertDialogTitle>
        <AlertDialogDescription>
          This will approve {withAccount} transaction{withAccount !== 1 ? 's' : ''} that have an assigned account.
          {withoutAccount > 0 && (
            <span> {withoutAccount} transaction{withoutAccount !== 1 ? 's' : ''} without an account will be skipped.</span>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={approving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-all-btn">
          {approving ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Approving...</span> : 'Approve All'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ApproveAndPostDialog = ({ open, onOpenChange, withoutAccount, onConfirm, posting }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Approve & Post All to General Ledger?</AlertDialogTitle>
        <AlertDialogDescription>
          This will approve all pending transactions and immediately post them to the General Ledger as double-entry journal entries.
          {withoutAccount > 0 && (
            <span className="block mt-1 text-amber-600 font-medium">
              {withoutAccount} transaction{withoutAccount !== 1 ? 's' : ''} without an account will be skipped.
            </span>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={posting} className="bg-[#D94E18] hover:bg-[#C2410C]" data-testid="confirm-approve-and-post-btn">
          {posting ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Processing...</span> : 'Approve & Post'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ReverseDialog = ({ transaction, onOpenChange, onConfirm }) => (
  <AlertDialog open={!!transaction} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Reverse Posting?</AlertDialogTitle>
        <AlertDialogDescription>
          This will create a reversing journal entry for this transaction. 
          The transaction will return to "Approved" status and can be posted again.
          <div className="mt-2 p-2 bg-zinc-50 rounded text-sm">
            <p className="font-medium">{transaction?.description}</p>
            <p className={getAmountClass(transaction?.amount || 0)}>{formatCurrency(transaction?.amount || 0)}</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reverse-btn">Reverse Posting</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const DeleteDialog = ({ transaction, onOpenChange, onConfirm }) => (
  <AlertDialog open={!!transaction} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete Transaction?</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete this transaction? This action cannot be undone.
          <div className="mt-2 p-2 bg-zinc-50 rounded text-sm">
            <p className="font-medium">{transaction?.description}</p>
            <p className={getAmountClass(transaction?.amount || 0)}>{formatCurrency(transaction?.amount || 0)}</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-txn-btn">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const NewAccountDialog = ({ open, onOpenChange, data, setData, currencies, accountTypes, onCreate }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Create New Account</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Code</Label>
            <Input value={data.code} onChange={(e) => setData(prev => ({ ...prev, code: e.target.value }))} placeholder="5000" data-testid="new-account-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select value={data.currency} onValueChange={(value) => setData(prev => ({ ...prev, currency: value }))}>
              <SelectTrigger data-testid="new-account-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => <SelectItem key={curr} value={curr}>{curr}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input value={data.name} onChange={(e) => setData(prev => ({ ...prev, name: e.target.value }))} placeholder="Account name" data-testid="new-account-name" />
        </div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select value={data.account_type} onValueChange={(value) => setData(prev => ({ ...prev, account_type: value }))}>
            <SelectTrigger data-testid="new-account-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {accountTypes.map((type) => <SelectItem key={type} value={type}>{accountTypeLabels[type]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Description (optional)</Label>
          <Input value={data.description} onChange={(e) => setData(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" data-testid="new-account-description" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onCreate} disabled={!data.code || !data.name} className="bg-[#D94E18] hover:bg-[#C2410C]" data-testid="create-account-btn">Create Account</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
